import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CurrencyInput } from '@/components/ui/currency-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Play,
  FileText,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Save,
  X,
  RotateCcw,
  FileSpreadsheet,
  MoreVertical,
  Share2,
  Plus,
  Trash2,
  Settings2,
  PlusCircle,
  Search,
} from 'lucide-react';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import type { Asset } from '@/types/asset';
import { dataService, getDataSourceMode } from '@/services/data-service';
import { formatCurrency } from '@/lib/format-currency';
import { mockAdditionBatches, type AdditionBatch, type AdditionItem } from '@/lib/mock-additions-data';

const getDataSource = getDataSourceMode;

export default function AdditionsPage(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<AdditionBatch | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [modifiedHighlight, setModifiedHighlight] = useState(false);
  const [completedBatches, setCompletedBatches] = useState<AdditionBatch[]>(mockAdditionBatches);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalBatch, setOriginalBatch] = useState<AdditionBatch | null>(null);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchBatches = async () => {
      setIsLoadingBatches(true);
      try {
        // Fetch all batches (both completed and in-progress)
        const batches = await dataService.getAdditionBatches();
        if (batches && batches.length > 0) {
          const sortedBatches = batches.sort((a: any, b: any) => 
            new Date(b.dateOfRun || b.createdDate).getTime() - new Date(a.dateOfRun || a.createdDate).getTime()
          );
          setCompletedBatches(sortedBatches);
        } else {
          // Only show mock data in demo mode
          if (getDataSource() === 'demo') {
            setCompletedBatches(mockAdditionBatches);
          } else {
            setCompletedBatches([]);
          }
        }
      } catch (error) {
        console.error('Error fetching addition batches:', error);
        // Only show mock data in demo mode
        if (getDataSource() === 'demo') {
          setCompletedBatches(mockAdditionBatches);
        } else {
          setCompletedBatches([]);
        }
      } finally {
        setIsLoadingBatches(false);
      }
    };

    if (!currentBatch) {
      fetchBatches();
    }
  }, [currentBatch]);

  // Handle selected assets passed from assets page
  useEffect(() => {
    const state = location.state as { selectedAssets?: Asset[] } | null;
    if (state?.selectedAssets && state.selectedAssets.length > 0) {
      handleAssetsSelected(state.selectedAssets);
      // Clear the state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleStartRun = () => {
    setShowAssetSelection(true);
  };

  const handleAddMoreAssets = () => {
    setShowAssetSelection(true);
  };

  const handleRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    
    const updatedItems = currentBatch.items.filter(item => !selectedItemIds.has(item.id));
    const totalValue = updatedItems.reduce((sum, item) => sum + item.value, 0);
    
    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalValue,
    });
    
    setSelectedItemIds(new Set());
  };

  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Create addition items from selected assets
    const newItems: AdditionItem[] = selectedAssets.map((asset, index) => ({
      id: `add-item-${Date.now()}-${index}`,
      assetId: asset.id,
      assetCode: asset.assetId,
      assetDescription: asset.name,
      category: asset.category || '',
      location: asset.location || '',
      value: 0,
      notes: '',
    }));

    // If we have an existing batch, add to it; otherwise create new batch
    if (currentBatch) {
      // Filter out duplicates based on assetId
      const existingAssetIds = new Set(currentBatch.items.map(item => item.assetId));
      const uniqueNewItems = newItems.filter(item => !existingAssetIds.has(item.assetId));
      
      const allItems = [...currentBatch.items, ...uniqueNewItems];
      const totalValue = allItems.reduce((sum, item) => sum + item.value, 0);
      
      setCurrentBatch({
        ...currentBatch,
        items: allItems,
        totalValue,
      });
    } else {
      // Create new batch
      const totalValue = newItems.reduce((sum, item) => sum + item.value, 0);

      const now = new Date().toISOString();
      const batch: AdditionBatch = {
        id: `batch-${Date.now()}`,
        batchReference: '',
        dateOfRun: new Date().toISOString().split('T')[0],
        periodType: 'current',
        status: 'In Progress',
        notes: '',
        items: newItems,
        preparedBy: 'Current User',
        preparedDate: now,
        totalValue,
        currency: 'GBP',
        createdBy: 'Current User',
        createdDate: now,
        modifiedBy: 'Current User',
        modifiedDate: now,
      };

      setCurrentBatch(batch);
    }
    
    setShowAssetSelection(false);
  };

  const handleUpdateItem = (itemId: string, value: number) => {
    if (!currentBatch) return;

    const updatedItems = currentBatch.items.map((item) =>
      item.id === itemId ? { ...item, value } : item
    );

    const totalValue = updatedItems.reduce((sum, item) => sum + item.value, 0);

    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalValue,
    });
    setHasUnsavedChanges(true);
  };

  const isServerGeneratedId = (id: string) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(id);
  };

  const handleSave = async () => {
    if (!currentBatch) return;
    
    try {
      const now = new Date().toISOString();
      const batchToSave = {
        ...currentBatch,
        modifiedBy: 'Current User',
        modifiedDate: now,
        status: currentBatch.status || 'In Progress',
      };
      
      if (currentBatch.id && isServerGeneratedId(currentBatch.id)) {
        const updatedBatch = await dataService.updateAdditionBatch(currentBatch.id, batchToSave);
        // Ensure date is in YYYY-MM-DD format for date input
        const formattedBatch = {
          ...updatedBatch,
          dateOfRun: updatedBatch.dateOfRun.split('T')[0],
        };
        setCurrentBatch(formattedBatch);
      } else {
        const savedBatch = await dataService.createAdditionBatch(batchToSave);
        // Ensure date is in YYYY-MM-DD format for date input
        const formattedBatch = {
          ...savedBatch,
          dateOfRun: savedBatch.dateOfRun.split('T')[0],
        };
        setCurrentBatch(formattedBatch);
      }
      
      setHasUnsavedChanges(false);
      setOriginalBatch(JSON.parse(JSON.stringify(batchToSave)));
      setModifiedHighlight(true);
      setTimeout(() => setModifiedHighlight(false), 2000);
      
      toast.success('Batch saved', { description: 'Addition batch saved successfully' });
    } catch (error) {
      console.error('Failed to save addition batch:', error);
      toast.error('Failed to save batch', { description: 'Please ensure you are in API mode and try again.' });
    }
  };

  const handleProcess = () => {
    if (!currentBatch) return;
    
    // Validate mandatory fields
    if (!currentBatch.batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before processing.',
      });
      return;
    }
    
    if (!currentBatch.dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before processing.',
      });
      return;
    }
    
    setShowProcessConfirm(true);
  };

  const confirmProcess = async () => {
    if (!currentBatch) return;
    
    const reference = currentBatch.batchReference;
    
    try {
      // Create a transaction for each item in the batch
      for (const item of currentBatch.items) {
        await dataService.createTransaction({
          transactionDate: currentBatch.dateOfRun,
          type: 'addition',
          assetId: item.assetId,
          amount: item.value,
          currency: currentBatch.currency,
          status: 'posted',
          reference: currentBatch.batchReference,
          description: `Asset addition - ${currentBatch.batchReference || 'Batch'} (${currentBatch.periodType === 'current' ? 'Current Period' : 'Service Period'})`,
          preparedBy: currentBatch.preparedBy,
          preparedDate: currentBatch.preparedDate,
          postedBy: 'Current User',
          postedDate: new Date().toISOString(),
        });
      }
      
      // Save the batch to the database with completed status
      const completedBatch = {
        ...currentBatch,
        status: 'Completed',
        postedBy: 'Current User',
        postedDate: new Date().toISOString(),
      };
      
      // Create or update the batch in the database
      if (currentBatch.id && isServerGeneratedId(currentBatch.id)) {
        await dataService.updateAdditionBatch(currentBatch.id, completedBatch);
      } else {
        await dataService.createAdditionBatch(completedBatch);
      }
      
      // Show success toast
      toast.success('Addition Created Successfully', {
        description: `Reference: ${reference}`,
      });
      
      // Clear batch and navigate back to processing screen
      setCurrentBatch(null);
      setSelectedItemIds(new Set());
      setShowProcessConfirm(false);
      
      // Navigate back to processing screen
      navigate('/transactions/additions');
    } catch (error) {
      console.error('Failed to process addition batch:', error);
      toast.error('Failed to process addition batch', {
        description: 'Please ensure you are in API mode and try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  const handleCancelBatch = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      // If no unsaved changes, just go back
      setCurrentBatch(null);
    }
  };

  const confirmCancelBatch = () => {
    setCurrentBatch(null);
    setHasUnsavedChanges(false);
  };

  // Filter batches based on search query
  const filteredBatches = completedBatches.filter((batch) => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      batch.batchReference.toLowerCase().includes(query) ||
      batch.notes.toLowerCase().includes(query) ||
      batch.preparedBy.toLowerCase().includes(query) ||
      batch.postedBy?.toLowerCase().includes(query) ||
      batch.status.toLowerCase().includes(query)
    );
  });

  // Landing Dashboard View
  if (!currentBatch) {
    return (
      <div className="flex h-full flex-col">
        {/* Page Header */}
        <div className="border-b bg-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <PlusCircle className="h-6 w-6 text-primary" />
                Asset Additions
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Process multiple asset additions in a single batch transaction</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowComingSoon(true)}>
                <FileText className="mr-2 h-4 w-4" />
                View Last Batch Summary
              </Button>
              <Button onClick={handleStartRun}>
                <Play className="mr-2 h-4 w-4" />
                Start New Addition Batch
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
        {/* Recent Batches */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold">Transaction History</h2>
              <p className="text-sm text-muted-foreground mt-1">All addition batches (in progress and completed)</p>
            </div>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>
          </div>
          
          <div className="space-y-3">
            {isLoadingBatches ? (
              <div className="text-center py-8 text-muted-foreground">Loading batches...</div>
            ) : filteredBatches.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? 'No transactions match your search' : 'No batches found'}
              </div>
            ) : (
              filteredBatches.map((batch) => (
                <div key={batch.id} className="flex items-center justify-between rounded-lg border border-border p-4 bg-background hover:bg-muted/50 transition-colors shadow-sm">
                  <div className="space-y-1">
                    <div className="font-semibold">{batch.batchReference}</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(batch.dateOfRun).toLocaleDateString('en-GB')} • £
                      {batch.totalValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })} • {batch.periodType === 'current' ? 'Current Period' : 'Service Period'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Posted by {batch.postedBy} on {batch.postedDate && new Date(batch.postedDate).toLocaleString('en-GB')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="secondary"
                      className={
                        batch.status === 'Completed'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }
                    >
                      {batch.status === 'Completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {batch.status === 'In Progress' && <Clock className="h-3 w-3 mr-1" />}
                      {batch.status}
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={async () => {
                        // Load full batch data including items
                        try {
                          if (batch.id && isServerGeneratedId(batch.id)) {
                            const fullBatch = await dataService.getAdditionBatch(batch.id);
                            // Ensure date is in YYYY-MM-DD format for date input
                            const formattedBatch = {
                              ...fullBatch,
                              dateOfRun: fullBatch.dateOfRun.split('T')[0],
                            };
                            setCurrentBatch(formattedBatch);
                            setOriginalBatch(JSON.parse(JSON.stringify(formattedBatch)));
                            setHasUnsavedChanges(false);
                          } else {
                            setCurrentBatch(batch);
                            setOriginalBatch(JSON.parse(JSON.stringify(batch)));
                            setHasUnsavedChanges(false);
                          }
                        } catch (error) {
                          console.error('Error loading batch:', error);
                          setCurrentBatch(batch);
                          setOriginalBatch(JSON.parse(JSON.stringify(batch)));
                          setHasUnsavedChanges(false);
                        }
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

          </div>
        </div>

        <AssetSelectionDialog
          open={showAssetSelection}
          onOpenChange={setShowAssetSelection}
          onConfirm={handleAssetsSelected}
          title="Select Assets for Addition"
          description="Choose one or more assets to add to this addition batch."
          excludeAssetIds={[]}
        />

        <ComingSoonModal
          open={showComingSoon}
          onOpenChange={setShowComingSoon}
          title="Batch Summary Coming Soon"
          description="The ability to view the last batch summary will be available in an upcoming release."
        />
      </div>
    );
  }

  // Batch Processing View
  const readonly = currentBatch.status === 'Completed';
  const allSelected = currentBatch.items.length > 0 && selectedItemIds.size === currentBatch.items.length;
  const someSelected = selectedItemIds.size > 0 && selectedItemIds.size < currentBatch.items.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItemIds(new Set(currentBatch.items.map((item) => item.id)));
    } else {
      setSelectedItemIds(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    const newSelected = new Set(selectedItemIds);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItemIds(newSelected);
  };

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex-1 overflow-auto pb-32">
        {/* Page Header */}
        <div className="bg-card p-6 pb-0">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <PlusCircle className="h-6 w-6 text-primary" />
                Asset Additions
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Last Modified <span 
                  className={`font-semibold transition-colors duration-2000 ${
                    modifiedHighlight ? 'text-green-600 dark:text-green-400' : 'text-foreground'
                  }`}
                >
                  {currentBatch.modifiedDate ? (() => {
                    const date = new Date(currentBatch.modifiedDate);
                    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                    const getOrdinal = (n: number) => {
                      const s = ['th', 'st', 'nd', 'rd'];
                      const v = n % 100;
                      return n + (s[(v - 20) % 10] || s[v] || s[0]);
                    };
                    return `${days[date.getDay()]} ${getOrdinal(date.getDate())} ${months[date.getMonth()]} ${date.getFullYear()} ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                  })() : 'Not saved yet'}
                </span>
              </p>
            </div>
            <Badge
              variant="secondary"
              className={`text-base px-3 py-1 ${
                currentBatch.status === 'In Progress'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              }`}
            >
              {currentBatch.status === 'Completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
              {currentBatch.status === 'In Progress' && <Clock className="h-3 w-3 mr-1" />}
              {currentBatch.status}
            </Badge>
          </div>
          
          <div className="-mx-6 px-6 pt-6 pb-4 border-y border-border bg-[#F9FAFB] dark:bg-[#121212]">
            <div className="space-y-4">
              {/* Period Type Selection */}
              <div className="space-y-2">
                <Label>Period Type</Label>
                <RadioGroup
                  value={currentBatch.periodType}
                  onValueChange={(value: string) => setCurrentBatch({ ...currentBatch, periodType: value as 'current' | 'service' })}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="current" id="current" disabled={readonly} />
                    <Label htmlFor="current" className="font-normal cursor-pointer">
                      Current Period
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="service" id="service" disabled={readonly} />
                    <Label htmlFor="service" className="font-normal cursor-pointer">
                      Service Period
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex gap-4">
                {/* Left side: Reference and Date stacked */}
                <div className="flex flex-col gap-4 flex-shrink-0" style={{ width: '300px' }}>
                  <div className="space-y-2">
                    <Label htmlFor="batch-ref">
                      Reference <span className="text-red-500">*</span>
                    </Label>
                    <Input 
                      id="batch-ref" 
                      value={currentBatch.batchReference}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, batchReference: e.target.value })}
                      readOnly={readonly}
                      placeholder="Enter batch reference"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date-of-run">
                      Date <span className="text-red-500">*</span>
                    </Label>
                    <Input 
                      id="date-of-run" 
                      type="date" 
                      value={currentBatch.dateOfRun}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, dateOfRun: e.target.value })}
                      readOnly={readonly}
                      required
                    />
                  </div>
                </div>

                {/* Right side: Notes textarea spanning full height */}
                <div className="flex-1 flex flex-col">
                  <Label htmlFor="notes" className="mb-2">Notes</Label>
                  <Textarea
                    id="notes"
                    value={currentBatch.notes}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, notes: e.target.value })}
                    placeholder="Add notes about this addition batch..."
                    className="flex-1 resize-none h-[104px] bg-white dark:bg-gray-950"
                    readOnly={readonly}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detail Grid */}
        <div 
          className="bg-card" 
          style={{ 
            paddingLeft: 'calc(var(--spacing) * 6)',
            paddingRight: 'calc(var(--spacing) * 6)',
            paddingTop: 'calc(var(--spacing) * 6)',
            paddingBottom: 'calc(var(--spacing) * 6)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">Addition Details</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {currentBatch.items.length} {currentBatch.items.length === 1 ? 'asset' : 'assets'} added to this batch
              </p>
            </div>
            {!readonly && (
              <div className="flex items-center gap-3">
                <Button variant="default" className="h-[42px]" onClick={handleAddMoreAssets}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Assets
                </Button>
                {selectedItemIds.size > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Settings2 className="h-4 w-4 mr-2" />
                        Actions ({selectedItemIds.size})
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleRemoveSelectedItems} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove Selected
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="border-y border-border min-h-[calc(100vh-400px)] bg-[#F9FAFB] dark:bg-[#121212]" style={{ 
          paddingLeft: 'calc(var(--spacing) * 6)', 
          paddingRight: 'calc(var(--spacing) * 6)',
          paddingTop: '10px',
          paddingBottom: '10px'
        }}>
          
          {currentBatch.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg">
              <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Assets Added</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Click "Add Assets" to select assets for this addition batch
              </p>
              <Button onClick={handleAddMoreAssets}>
                <Plus className="mr-2 h-4 w-4" />
                Add Assets
              </Button>
            </div>
          ) : (
            <div className="border bg-white dark:bg-black">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px] pl-6">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                        disabled={readonly}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead className="w-[150px]">Asset Code</TableHead>
                    <TableHead className="min-w-[300px]">Asset Description</TableHead>
                    <TableHead className="w-[200px] pr-6">Value (£)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentBatch.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="w-[40px] pl-6">
                        <Checkbox
                          checked={selectedItemIds.has(item.id)}
                          onCheckedChange={(checked) => handleSelectItem(item.id, checked as boolean)}
                          disabled={readonly}
                          aria-label={`Select ${item.assetCode}`}
                        />
                      </TableCell>
                      <TableCell className="w-[150px] font-mono text-sm">
                        {item.assetCode}
                      </TableCell>
                      <TableCell className="min-w-[300px]">
                        {item.assetDescription}
                      </TableCell>
                      <TableCell className="w-[200px] pr-6">
                        <CurrencyInput
                          value={item.value}
                          onChange={(value) => handleUpdateItem(item.id, value)}
                          className="w-full"
                          readOnly={readonly}
                          disabled={readonly}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        
        <AssetSelectionDialog
          open={showAssetSelection}
          onOpenChange={setShowAssetSelection}
          onConfirm={handleAssetsSelected}
          title="Select Assets for Addition"
          description="Choose one or more assets to add to this addition batch."
          excludeAssetIds={currentBatch?.items?.map((item: any) => item.assetId) || []}
        />

        {/* Confirmation Modals */}
        <ConfirmationModal
          open={showCancelConfirm}
          onOpenChange={setShowCancelConfirm}
          onConfirm={confirmCancelBatch}
          title="Are you sure you wish to leave this transaction?"
          description="All unsaved changes will be lost"
          confirmText="Yes, Leave"
          cancelText="Stay"
          type="warning"
        />

        <ConfirmationModal
          open={showProcessConfirm}
          onOpenChange={setShowProcessConfirm}
          onConfirm={confirmProcess}
          title="Process Addition Batch"
          description={
            <div className="space-y-2">
              <p>This will add the assets to the register.</p>
              <div className="mt-4 p-3 bg-muted rounded-md space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Assets:</span>
                  <span className="font-medium">{currentBatch?.items.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Value:</span>
                  <span className="font-medium">£{currentBatch?.totalValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-3">This action cannot be undone.</p>
            </div>
          }
          confirmText="Process Batch"
          cancelText="Review Again"
          type="success"
        />

        <ConfirmationModal
          open={showRemoveConfirm}
          onOpenChange={setShowRemoveConfirm}
          onConfirm={confirmRemoveSelectedItems}
          title="Remove Selected Assets"
          description={`Are you sure you want to remove ${selectedItemIds.size} selected asset(s) from this batch? This action cannot be undone.`}
          confirmText="Remove Assets"
          cancelText="Cancel"
          type="danger"
        />
      </div>

      {/* Sticky Footer - Pinned to bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-background border-t shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Action Buttons */}
            <div className="flex gap-2">
              {currentBatch.status === 'In Progress' && (
                <>
                  <Button variant="outline" onClick={handleCancelBatch}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button variant="outline" onClick={handleSave}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={handleProcess}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Process
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top">
                      <DropdownMenuItem>
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Export to Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
              {currentBatch.status === 'Completed' && (
                <>
                  <Button variant="outline" onClick={() => setCurrentBatch(null)}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Back to Dashboard
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top">
                      <DropdownMenuItem>
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Export to Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>

            {/* Center: Empty space */}
            <div className="flex-1 flex items-center justify-center">
            </div>

            {/* Right: Summary Totals */}
            <div className="flex gap-8">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total Assets</div>
                <div className="text-lg font-bold">
                  {currentBatch.items.length}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total Value</div>
                <div className="text-lg font-bold text-green-600">
                  £{currentBatch.totalValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
